/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {
    Stack,
    Text
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import PropTypes from 'prop-types'
import React from 'react'
import {FormattedMessage, FormattedNumber, useIntl} from 'react-intl'
import {useItemVariant} from '.'
import CurrencyFormatter from '../currency-formatter/CurrencyFormatter'

const PricePerItem = ({currency, basket, basePrice}) => {
    const {currency: activeCurrency} = useCurrency()
    return (
        <Text fontSize={{base: '12px', lg: '14px'}}>
            <FormattedNumber
                style="currency"
                currency={currency || basket?.currency || activeCurrency}
                value={basePrice}
            />
            <FormattedMessage
                defaultMessage="ea"
                id="price_per_item.label.each"
                description="Abbreviated 'each', follows price per item, like $10/ea"
            />
        </Text>
    )
}

PricePerItem.propTypes = {
    currency: PropTypes.string,
    basket: PropTypes.object,
    basePrice: PropTypes.number
}

/**
 * In the context of a cart product item variant, this component renders the item's
 * pricing, taking into account applied discounts as well as base item prices.
 */
const ItemPrice = ({
    currency,
    align = 'right',
    baseDirection = 'column',
    ...props
}) => {
    const {variant} = useItemVariant()
    const {data: basket} = useCurrentBasket()
    const {currency: activeCurrency} = useCurrency()
    const intl = useIntl()

    const {price, priceAfterItemDiscount, c_originalPrice = null} = variant
    const isProductASet = variant?.type?.set

    const displayPrice =
        typeof priceAfterItemDiscount === 'number'
            ? Math.min(price, priceAfterItemDiscount)
            : price
    const originalPrice = c_originalPrice || price

    const hasDiscount = displayPrice < originalPrice * (variant?.quantity || 1)

    return (
        <Stack
            textAlign="left"
            direction={
                hasDiscount ? 'column' : {base: baseDirection, lg: 'row'}
            }
            justifyContent={align === 'left' ? 'flex-start' : 'flex-end'}
            alignItems="baseline"
            spacing={hasDiscount ? 0 : 1}
            wrap="nowrap"
            {...props}
        >
            <Text>
                {isProductASet &&
                    `${intl.formatMessage({
                        defaultMessage: 'Starting at',
                        id: 'item_price.label.starting_at'
                    })} `}

                <CurrencyFormatter
                    currency={currency || basket?.currency || activeCurrency}
                    value={displayPrice}
                />
                {hasDiscount && (
                    <Text
                        as="span"
                        fontSize="sm"
                        fontWeight="normal"
                        textDecoration="line-through"
                        color="#782B2F"
                        marginLeft={1}
                    >
                        <CurrencyFormatter
                            value={originalPrice}
                            currency={
                                currency || basket?.currency || activeCurrency
                            }
                        />
                    </Text>
                )}
            </Text>
        </Stack>
    )
}

ItemPrice.propTypes = {
    currency: PropTypes.string,
    align: PropTypes.string,
    baseDirection: PropTypes.string
}

export default ItemPrice
